import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useRegistryGiftBasketCountByEventNameQuery } from './RegistryGiftBasketWidget.graphql.generated';
import { useDisclosure } from '@withjoy/joykit';
import { useControllableState } from '@shared/utils/hooks/useControllableState';
import type { GiftBasketTab, UseRegistryGiftTrackerWidgetArgs, UseRegistryGiftTrackerWidgetRootArgs } from './RegistryGiftBasketWidget.types';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { flattenedValidRegistryOrders, useDeferredRedirect } from './RegistryGiftBasketWidget.utils';
import { MAX_ITEM_COUNT } from './components/GiftBasketContent/GiftBasketContent.constants';
import { useRegistryGiftBasketWidgetTelemetry } from './RegistryGiftBasketWidget.telemetry';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { withWindow } from '@shared/utils/withWindow';
import { getLocalStorage, useHistory, useLocation, useRouteMatch } from '@shared/core';
import { useEnableChecklist } from './hooks';
import globalWindow from '@shared/core/globals';
import { useRegistryChecklistTelemetry } from '../RegistryChecklist/RegistryChecklist.telemetry';
import { useRoutePaths } from '@apps/registry/admin/Registry.routes';
import { getTotalRegistryItemsCount } from '../../utils/totalRegistryItemsCount';
import { CartCheckoutSteps } from '../ShoppingCart';
import { useShoppingCart } from '../ShoppingCart/state';
import { createRegistryItemSubject } from '../Catalog';
import { AddCatalogItemPurchaseMethod } from '../Catalog/components/ProductDetails/components/BuyForm/BuyForm.types';

const CHECKLIST_DISMISSED_STORAGE_KEY = 'joyRegistry_hasDismissedChecklistPopover';
const HIDE_CHECKLIST_NAVIGATION_CHANGE_ATTEMPTS = 4;
const HIDE_CHECKLIST_NAVIGATION_STORAGE_KEY = 'joyRegistry_hideChecklistNavigationAttempts';

export const useRegistryGiftTrackerWidget = (args: UseRegistryGiftTrackerWidgetArgs) => {
  const { eventHandle } = args;
  const { data, loading } = useRegistryGiftBasketCountByEventNameQuery({ batchMode: 'fast', variables: { handle: eventHandle }, skip: !eventHandle });
  return {
    data: data?.eventByName,
    loading,
  };
};

const TAB_SET = new Set<GiftBasketTab>(['giftTracker', 'giftList', 'checklist', 'shoppingCart']);

export const CHECKLIST_VISIBILITY_QUERY_PARAM = 'checklistVisible';

const isValidManagePageTab = (maybeTab: string | undefined = ''): maybeTab is GiftBasketTab => TAB_SET.has(maybeTab as GiftBasketTab);

export const useRegistryGiftTrackerWidgetRoot = (args: UseRegistryGiftTrackerWidgetRootArgs) => {
  const { id: eventId, website, registry } = args.data;
  const { redirectToManageRegistryClick, giftBasketToggleClicked, giftBasketClicked } = useRegistryGiftBasketWidgetTelemetry();
  const enableChecklistInGiftBasketWidget = useEnableChecklist();
  const queryParams = useQueryParams();
  const isChecklistVisibleQueryParamTrue = queryParams[CHECKLIST_VISIBILITY_QUERY_PARAM] === 'true';
  const history = useHistory();
  const location = useLocation();
  const { onChecklistTooltipShown, onChecklistTooltipRespond } = useRegistryChecklistTelemetry();
  const {
    state: { currentCheckoutStep },
    mutators: { addItem: addItemToCart, buyNowFromShoppingCart },
  } = useShoppingCart();

  const routes = useRoutePaths();
  const isShopOrQuickAddRouteMatch = useRouteMatch({ path: [routes.catalog.path, routes.quickAdd.path] });
  const isTrackRouteMatch = useRouteMatch({ path: routes.track.path, exact: true });
  const isManageRouteMatch = useRouteMatch({ path: routes.manage.path, exact: true });
  const page = isShopOrQuickAddRouteMatch ? 'shop' : isTrackRouteMatch ? 'track' : isManageRouteMatch ? 'manage' : 'unknown';

  const storage = getLocalStorage();
  const hasNullChecklistPopoverStorageValue = storage.getItem(CHECKLIST_DISMISSED_STORAGE_KEY) === null;
  const hasDismissedChecklistPopover = storage.getItem(CHECKLIST_DISMISSED_STORAGE_KEY) === 'true';

  const { deferredAdminRegManageRedirect } = useDeferredRedirect();

  const { isOpen: tryChecklistPopoverIsOpen, onOpen: onOpenPopover, onClose: handleOnCloseChecklistPopover } = useDisclosure();

  const onCloseChecklistPopover = useEventCallback(() => {
    handleOnCloseChecklistPopover();
    storage.setItem(CHECKLIST_DISMISSED_STORAGE_KEY, 'true');
  });

  useEffect(() => {
    const sub = createRegistryItemSubject.subscribe(data => {
      const priceInMinorUnits = data.item.productData.price?.valueInMinorUnits;
      if (!priceInMinorUnits || !data.purchaseMethod) {
        return;
      }
      const lineItem = {
        priceInMinorUnits,
        quantity: data.item.totalRequested ?? 1,
        registryItemId: data.item.id,
      };
      switch (data.purchaseMethod) {
        case AddCatalogItemPurchaseMethod.addToCart:
          addItemToCart(lineItem, 10);
          break;
        case AddCatalogItemPurchaseMethod.buyNow:
          buyNowFromShoppingCart(lineItem, 10);
          break;
      }
    });

    return () => {
      sub.unsubscribe();
    };
  }, [addItemToCart, buyNowFromShoppingCart]);

  // We only want to show the Checklist popover once, but instead of using `defaultIsOpen` in `useDisclosure` we are doing
  // it manually for better interaction.
  // We'll only show checklist popover if the user has not dismissed it, and if it's the fifth time the user
  // lands on a page that should show it.
  useEffect(() => {
    if (args.hideChecklistPopover) {
      return;
    }

    let timeoutId: number | undefined;

    if (hasNullChecklistPopoverStorageValue) {
      storage.setItem(CHECKLIST_DISMISSED_STORAGE_KEY, 'false');
      storage.setItem(HIDE_CHECKLIST_NAVIGATION_STORAGE_KEY, '1');
      return;
    }

    const hideChecklistNavigationAttempts = parseInt(storage.getItem(HIDE_CHECKLIST_NAVIGATION_STORAGE_KEY) || '0');

    if (!hasDismissedChecklistPopover && hideChecklistNavigationAttempts < HIDE_CHECKLIST_NAVIGATION_CHANGE_ATTEMPTS) {
      storage.setItem(HIDE_CHECKLIST_NAVIGATION_STORAGE_KEY, `${hideChecklistNavigationAttempts + 1}`);
      return;
    }

    if (!registry.checklist?.id && !tryChecklistPopoverIsOpen && !hasDismissedChecklistPopover) {
      timeoutId = globalWindow.setTimeout(() => {
        onChecklistTooltipShown({ page });
        onOpenPopover();
      }, 1000);
    }

    return () => {
      globalWindow.clearTimeout(timeoutId);
    };
    // Run this effect on mount and when the route changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const isMobileOrTablet = useIsMobileOrTablet();

  const { registryItemsDto, registryOrdersDto } = useMemo(() => {
    return {
      registryItemsDto: {
        itemIds: [...(registry.items || [])]
          .sort((itemA, itemB) => itemB.createAt.milliseconds - itemA.createAt.milliseconds)
          .slice(0, MAX_ITEM_COUNT)
          .map(item => item.id),
        totalRegistryItemsCount: getTotalRegistryItemsCount(registry.items),
      },
      registryOrdersDto: flattenedValidRegistryOrders(registry.orders),
    };
  }, [registry]);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentTab, setOverlayTab] = useControllableState<GiftBasketTab>({ defaultValue: 'giftList' });

  const isCheckoutInitiated = currentCheckoutStep !== CartCheckoutSteps.Cart;
  useEffect(() => {
    if (!isOpen && isCheckoutInitiated) {
      setOverlayTab('shoppingCart');
      onOpen();
    }
  }, [isCheckoutInitiated, onOpen, isOpen, setOverlayTab]);

  const trySetOverlayTab = useCallback(
    (maybeValidTag: string | undefined = ''): maybeValidTag is GiftBasketTab => {
      const isValid = isValidManagePageTab(maybeValidTag);
      if (isValid) {
        setOverlayTab(maybeValidTag as GiftBasketTab);
      }
      return isValid;
    },
    [setOverlayTab],
  );

  useEffect(() => {
    if (enableChecklistInGiftBasketWidget && isChecklistVisibleQueryParamTrue) {
      const tab = 'checklist';
      trySetOverlayTab(tab);
      giftBasketClicked({ overlay: 'toOpen', label: tab, method: 'queryParam' });
      onOpen();

      if (tryChecklistPopoverIsOpen) {
        onCloseChecklistPopover();
      }

      const url = new URL(withWindow(global => global.location.href, ''));
      url.searchParams.delete(CHECKLIST_VISIBILITY_QUERY_PARAM);
      history.replace({ search: url.searchParams.toString() });
    }
  }, [
    enableChecklistInGiftBasketWidget,
    isChecklistVisibleQueryParamTrue,
    onOpen,
    trySetOverlayTab,
    history,
    tryChecklistPopoverIsOpen,
    onCloseChecklistPopover,
    giftBasketClicked,
  ]);

  const handleTriggerClick = useEventCallback<React.MouseEventHandler<HTMLButtonElement>>(e => {
    const tab = e.currentTarget.dataset.tab;
    if (tryChecklistPopoverIsOpen) {
      onCloseChecklistPopover();
    }
    if (isOpen) {
      giftBasketClicked({ overlay: 'toClose', label: currentTab, method: 'topNav' });
      onClose();
    } else {
      if (trySetOverlayTab(tab)) {
        giftBasketClicked({ overlay: 'toOpen', label: tab, method: 'topNav' });
        onOpen();
      }
    }
  });

  const handleOnClose = useEventCallback(() => {
    setTimeout(() => {
      giftBasketClicked({ overlay: 'toClose', label: currentTab, method: 'giftBasketDialog' });
      onClose();
    }, 100);
  });

  const handleOnSegmentedTabClick = useEventCallback<React.MouseEventHandler<HTMLButtonElement>>(e => {
    const tab = e.currentTarget.dataset.tab;
    if (trySetOverlayTab(tab)) {
      giftBasketToggleClicked({ label: tab });
    }
  });

  const handleOnVisitManageTabClick = useEventCallback<React.MouseEventHandler<HTMLButtonElement>>(e => {
    const manageTab = e.currentTarget.dataset.manageTab;

    if (!isValidManagePageTab(manageTab)) {
      return;
    }

    // close the overlay (side-drawer)
    onClose();
    const basePathname = `/${website}/edit/registry/manage`;
    redirectToManageRegistryClick({ method: 'giftBasketDialog', label: manageTab });
    deferredAdminRegManageRedirect(basePathname, manageTab);
  });

  const handleOnTryChecklist = useCallback(() => {
    onCloseChecklistPopover();
    onChecklistTooltipRespond({ label: 'Try Checklist' });
    setOverlayTab('checklist');
    onOpen();
  }, [onOpen, onCloseChecklistPopover, setOverlayTab, onChecklistTooltipRespond]);

  const handleOnNoThanksToChecklist = useCallback(() => {
    onCloseChecklistPopover();
    onChecklistTooltipRespond({ label: 'No Thanks' });
  }, [onCloseChecklistPopover, onChecklistTooltipRespond]);

  return {
    currentTab,
    eventId,
    website,
    handleTriggerClick,
    handleOnVisitManageTabClick,
    handleOnSegmentedTabClick,
    isOpen,
    isMobileOrTablet,
    onClose: handleOnClose,
    registryItemsDto,
    registryOrdersDto,
    tryChecklistPopoverIsOpen,
    handleOnNoThanksToChecklist,
    handleOnTryChecklist,
    isCheckoutInitiated,
  };
};
